import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { publicApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState("");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer = null;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await publicApi.get(`/checkout/status/${sessionId}`);
        setStatus(data);
        setAttempts(n);
        if (data.payment_status === "paid" || data.status === "completed") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (n < MAX_POLLS) {
          timer = setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Could not check your order status");
      }
    };
    poll(1);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId, clear]);

  const isPaid = status.payment_status === "paid" || status.status === "completed";
  const isTimeout = attempts >= MAX_POLLS && !isPaid;

  return (
    <div className="mx-auto max-w-[720px] px-6 pt-32 pb-24 text-center" data-testid="payment-success-page">
      {isPaid ? (
        <>
          <div className="mx-auto mb-6" style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(200,155,60,.15)", display: "grid", placeItems: "center", border: "1px solid var(--gold)" }}>
            <CheckCircle2 size={40} color="var(--gold)" />
          </div>
          <p className="font-caps text-gold mb-4" style={{ fontSize: ".65rem", letterSpacing: ".28em" }}>Order confirmed</p>
          <h1 className="font-display mb-4" style={{ fontSize: "2.6rem", color: "var(--parchment)" }}>Thank you.</h1>
          <p style={{ color: "var(--ash)", fontSize: "1rem", lineHeight: 1.6 }}>
            A receipt is on its way to your inbox. We&rsquo;ll email tracking as soon as your order ships.
          </p>
          {status.items?.length > 0 && (
            <div className="mt-8 p-5 mx-auto text-left" style={{ maxWidth: 480, background: "var(--ink-raised)", border: "var(--rule)", borderRadius: "var(--radius)" }} data-testid="order-summary">
              <p className="font-caps text-gold mb-3" style={{ fontSize: ".55rem" }}>Order summary</p>
              {status.items.map((it) => (
                <div key={it.id} className="flex justify-between py-2" style={{ borderTop: "var(--rule)" }}>
                  <span style={{ color: "var(--parchment)" }}>{it.name} × {it.quantity}</span>
                  <span className="price-tag" style={{ fontSize: ".9rem" }}>${(it.price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-10 flex gap-4 justify-center">
            <Link to="/merch" className="btn-gold" data-testid="continue-shopping">Continue shopping</Link>
            <Link to="/" className="font-caps" style={{ fontSize: ".62rem", color: "var(--ash)", alignSelf: "center" }}>Back home</Link>
          </div>
        </>
      ) : isTimeout ? (
        <>
          <div className="mx-auto mb-6" style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(200,155,60,.10)", display: "grid", placeItems: "center", border: "var(--rule)" }}>
            <Clock size={38} color="var(--ash)" />
          </div>
          <h1 className="font-display mb-4" style={{ fontSize: "2rem", color: "var(--parchment)" }}>Still processing…</h1>
          <p style={{ color: "var(--ash)" }}>Your payment is being finalized. You&rsquo;ll get an email as soon as it clears.</p>
          <Link to="/" className="btn-gold mt-8" data-testid="back-home">Back home</Link>
        </>
      ) : (
        <>
          <div className="mx-auto mb-6" style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(200,155,60,.10)", display: "grid", placeItems: "center", border: "var(--rule)" }}>
            <div className="animate-spin" style={{ width: 32, height: 32, border: "3px solid transparent", borderTopColor: "var(--gold)", borderRadius: "50%" }} />
          </div>
          <h1 className="font-display" style={{ fontSize: "1.8rem", color: "var(--parchment)" }}>Finalizing your order…</h1>
          <p className="mt-2" style={{ color: "var(--ash)" }}>Hang tight — this only takes a moment.</p>
          {error && <p className="text-rose mt-4">{error}</p>}
        </>
      )}
    </div>
  );
}
